// axios interceptor to pass auth tokens into all c-requests
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios"
import { store } from "./store"

const ACCESS_TOKEN_ERROR_CODE = 44
const REFRESH_BEFORE_EXPIRY_MS = 30_000

const SKIP_ACCESS_TOKEN = new Set([
  "POST /auth/login",
  "POST /auth/signup",
  "POST /auth/verify-email",
  "POST /auth/logout",
  "GET /auth/auth-state",
  "POST /auth/access-token",
  "POST /emails/send-password-reset-email",
  "POST /profile/verify-reset-token",
  "PUT /profile/update-password",
  "GET /payments/paystack-verify",
  "GET /orders/get-order",
  "PUT /orders/update-order",
])

type AccessTokenConfig = InternalAxiosRequestConfig & {
  skipAccessToken?: boolean
  _retriedAccessToken?: boolean
}

let accessToken: string | null = null
let accessTokenExpiresAt = 0
let mintPromise: Promise<string | null> | null = null

const cRequest = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
})

export const clearAccessToken = () => {
  accessToken = null
  accessTokenExpiresAt = 0
}

const pathnameOf = (url?: string) => {
  if (!url) return ""
  try {
    const parsed = new URL(url, "http://localhost")
    const path = parsed.pathname
    if (path.length > 1 && path.endsWith("/")) {
      return path.slice(0, -1)
    }
    return path
  } catch {
    return ""
  }
}

const shouldSkipAccessToken = (config: AccessTokenConfig) => {
  if (config.skipAccessToken) return true
  const method = (config.method || "get").toUpperCase()
  return SKIP_ACCESS_TOKEN.has(`${method} ${pathnameOf(config.url)}`)
}

const requestAccessToken = async () => {
  const accessTokenPath = `${process.env.REACT_APP_BASE_URL}/auth/access-token`
  try {
    const response = await cRequest.post(
      accessTokenPath,
      {},
      { skipAccessToken: true } as AccessTokenConfig
    )
    const token = response?.data?.accessToken
    const expiresIn = response?.data?.expiresIn
    if (!token || !expiresIn) {
      clearAccessToken()
      return null
    }
    accessToken = token
    accessTokenExpiresAt = Date.now() + expiresIn * 1000
    return accessToken
  } catch {
    clearAccessToken()
    return null
  }
}

const ensureAccessToken = () => {
  const tokenIsFresh =
    accessToken && accessTokenExpiresAt - Date.now() > REFRESH_BEFORE_EXPIRY_MS
  if (tokenIsFresh) {
    return Promise.resolve(accessToken)
  }
  if (!mintPromise) {
    mintPromise = requestAccessToken().finally(() => {
      mintPromise = null
    })
  }
  return mintPromise
}

const isAccessTokenError = (error: AxiosError) => {
  const data = error.response?.data as { error?: number } | undefined
  return (
    error.response?.status === 401 && data?.error === ACCESS_TOKEN_ERROR_CODE
  )
}

cRequest.interceptors.request.use(async (config) => {
  const requestConfig = config as AccessTokenConfig
  const state = store.getState()
  const role = state?.global?.user?.role
  const token = `${process.env.REACT_APP_CP_API_KEY}`
  requestConfig.headers["Authorization"] = `Bearer ${token}`
  requestConfig.headers["role"] = role || "user"

  if (shouldSkipAccessToken(requestConfig)) {
    return requestConfig
  }

  const headerToken = await ensureAccessToken()
  if (headerToken) {
    requestConfig.headers["x-access-token"] = headerToken
  }
  return requestConfig
})

cRequest.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const requestConfig = error.config as AccessTokenConfig | undefined
    if (
      !requestConfig ||
      requestConfig._retriedAccessToken ||
      shouldSkipAccessToken(requestConfig) ||
      !isAccessTokenError(error)
    ) {
      return Promise.reject(error)
    }

    requestConfig._retriedAccessToken = true
    clearAccessToken()
    const headerToken = await ensureAccessToken()
    if (!headerToken) {
      return Promise.reject(error)
    }
    requestConfig.headers["x-access-token"] = headerToken
    return cRequest(requestConfig)
  }
)

export default cRequest
